import { useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import type { ReactElement } from 'react';

import type { Asset, PaginationLinks } from '../Pages/Section/hooks/useGetAssets';
import { useGetAssets } from '../Pages/Section/hooks/useGetAssets';

interface AssetPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: Asset) => void;
  organisationId: string;
  aspectRatio?: string;
}

function extractPage(url: string | null): number | null {
  if (!url) return null;
  const match = url.match(/[?&]page=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

export function AssetPickerModal({
  open,
  onClose,
  onSelect,
  organisationId,
  aspectRatio,
}: AssetPickerModalProps): ReactElement {
  const [page, setPage] = useState(1);
  const { assets, pagination, isLoading, isError } = useGetAssets({
    organisationId,
    aspectRatio,
    page,
    enabled: open,
  });

  function handleClose() {
    setPage(1);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box className="vtwo:flex vtwo:items-center vtwo:justify-between">
          <span>Select image</span>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box className="vtwo:flex vtwo:justify-center vtwo:py-24">
            <CircularProgress size={32} />
          </Box>
        )}

        {isError && (
          <Typography variant="body2" className="vtwo:text-red-500 vtwo:py-8">
            Failed to load assets.
          </Typography>
        )}

        {!isLoading && !isError && assets.length === 0 && (
          <Typography variant="body2" className="vtwo:text-gray-500 vtwo:py-8">
            No assets found.
          </Typography>
        )}

        {!isLoading && !isError && assets.length > 0 && (
          <Box className="vtwo:grid vtwo:grid-cols-1 vtwo:sm:grid-cols-3 vtwo:gap-12 vtwo:p-4">
            {assets.map((asset) => (
              <AssetCard key={asset.id} asset={asset} onSelect={() => onSelect(asset)} />
            ))}
          </Box>
        )}
      </DialogContent>

      {pagination && (
        <PaginationBar
          links={pagination.links}
          currentPage={pagination.currentPage}
          onPageChange={setPage}
        />
      )}
    </Dialog>
  );
}

interface PaginationBarProps {
  links: PaginationLinks;
  currentPage: number;
  onPageChange: (page: number) => void;
}

function PaginationBar({ links, currentPage, onPageChange }: PaginationBarProps): ReactElement {
  const firstPage = extractPage(links.first) ?? 1;
  const lastPage = extractPage(links.last);
  const prevPage = extractPage(links.prev);
  const nextPage = extractPage(links.next);

  if (!links.prev && !links.next) return <></>;

  return (
    <Box className="vtwo:flex vtwo:items-center vtwo:justify-center vtwo:gap-1 vtwo:py-2 vtwo:border-t vtwo:border-plus-border">
      {links.prev && (
        <IconButton size="small" onClick={() => onPageChange(firstPage)} title="First page">
          <FirstPageIcon fontSize="small" />
        </IconButton>
      )}
      {prevPage && (
        <IconButton size="small" onClick={() => onPageChange(prevPage)} title="Previous page">
          <NavigateBeforeIcon fontSize="small" />
        </IconButton>
      )}

      <Typography variant="body2" className="vtwo:text-gray-500 vtwo:px-2">
        Page {currentPage}
        {lastPage ? ` of ${lastPage}` : ''}
      </Typography>

      {nextPage && (
        <IconButton size="small" onClick={() => onPageChange(nextPage)} title="Next page">
          <NavigateNextIcon fontSize="small" />
        </IconButton>
      )}
      {links.next && lastPage && (
        <IconButton size="small" onClick={() => onPageChange(lastPage)} title="Last page">
          <LastPageIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

interface AssetCardProps {
  asset: Asset;
  onSelect: () => void;
}

function AssetCard({ asset, onSelect }: AssetCardProps): ReactElement {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        border: '2px solid #ECECEB',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': { borderColor: 'primary.main' },
        width: '100%',
        textAlign: 'left',
        padding: 0,
        background: 'none',
      }}
    >
      <Box className="vtwo:bg-gray-100 vtwo:h-36 vtwo:overflow-hidden">
        <img
          src={asset.thumbnailUrl}
          alt={asset.originalFilename}
          className="vtwo:w-full vtwo:h-full vtwo:object-cover vtwo:block"
        />
      </Box>
      <Typography
        variant="caption"
        className="vtwo:block vtwo:px-8 vtwo:py-4 vtwo:truncate vtwo:bg-white vtwo:text-gray-600"
      >
        {asset.originalFilename}
      </Typography>
    </Box>
  );
}
